import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import FacebookPixelService from '../services/facebookPixel';

const FiereForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    products: {
      milone: false,
      belpaese: false,
      contoTerzi: false
    },
    liquorTypes: [],
    bottleFormats: [],
    quantityRange: '',
    recipeType: '',
    recipeDetails: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const liquorOptions = [
    { value: 'amaro', label: 'Amaro' },
    { value: 'gin', label: 'Gin' },
    { value: 'limoncello', label: 'Limoncello' },
    { value: 'bitter', label: 'Bitter' },
    { value: 'grappa', label: 'Grappa' },
    { value: 'liquore_erbe', label: 'Liquore alle Erbe' },
    { value: 'altro', label: 'Altro' }
  ];

  const formatOptions = [
    { value: '5cl', label: '5cl - Mini' },
    { value: '10cl', label: '10cl - Tascabile' },
    { value: '20cl', label: '20cl - Mignon' },
    { value: '50cl', label: '50cl - Standard' },
    { value: '70cl', label: '70cl - Premium' },
    { value: '100cl', label: '100cl - Magnum' },
    { value: '150cl-bib', label: '150cl - Bag in Box' },
    { value: '3l-bib', label: '3 Litri - Bag in Box' },
    { value: '5l-bib', label: '5 Litri - Bag in Box' }
  ];

  const quantityOptions = [
    { value: '24-120', label: '24 - 120 bottiglie' },
    { value: '120-240', label: '120 - 240 bottiglie' },
    { value: '240-480', label: '240 - 480 bottiglie' },
    { value: '480+', label: 'Oltre 480 bottiglie' }
  ];

  const recipeOptions = [
    { value: 'personalizzata', label: 'Personalizzata' },
    { value: 'standard', label: 'Da vostre ricette Standard' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductCheckbox = (product) => {
    setFormData(prev => ({
      ...prev,
      products: {
        ...prev.products,
        [product]: !prev.products[product]
      }
    }));
  };

  const handleLiquorToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      liquorTypes: prev.liquorTypes.includes(value)
        ? prev.liquorTypes.filter(t => t !== value)
        : [...prev.liquorTypes, value]
    }));
  };

  const handleFormatToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      bottleFormats: prev.bottleFormats.includes(value)
        ? prev.bottleFormats.filter(f => f !== value)
        : [...prev.bottleFormats, value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Prepara i prodotti selezionati
    const selectedProducts = [];
    if (formData.products.milone) selectedProducts.push('Milone');
    if (formData.products.belpaese) selectedProducts.push('BelPaese');
    if (formData.products.contoTerzi) selectedProducts.push('Conto Terzi');

    // Prepara i tipi di liquore selezionati
    const liquorLabels = formData.liquorTypes.map(v => 
      liquorOptions.find(o => o.value === v)?.label || v
    );

    // Prepara i formati selezionati
    const formatLabels = formData.bottleFormats.map(v =>
      formatOptions.find(o => o.value === v)?.label || v
    );

    // Costruisci messaggio completo
    const recipeLabel = recipeOptions.find(o => o.value === formData.recipeType)?.label || formData.recipeType;
    const fullMessage = `
Contatto da Fiera Marca 2026

Prodotti interessati: ${selectedProducts.join(', ') || 'Non specificato'}
Tipi liquore: ${liquorLabels.join(', ') || 'Non specificato'}
Formati: ${formatLabels.join(', ') || 'Non specificato'}
Quantità: ${formData.quantityRange || 'Non specificato'}
Ricetta: ${recipeLabel || 'Non specificato'}${formData.recipeDetails ? ` - ${formData.recipeDetails}` : ''}

Note: ${formData.message || 'Nessuna'}
    `.trim();

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          liquorTypes: [...selectedProducts, ...liquorLabels],
          bottleFormat: formatLabels.join(', '),
          quantityRange: formData.quantityRange,
          message: fullMessage,
          userType: 'business'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante l\'invio');
      }

      const result = await response.json();
      
      if (result.success) {
        // Track Facebook Pixel - Lead event
        FacebookPixelService.trackLead({
          content_name: 'Marca 2026 Form',
          content_category: 'Fiera Lead'
        });

        setIsSuccess(true);
        toast({
          title: "Richiesta inviata!",
          description: "Grazie per averci contattato. Ti ricontatteremo presto!",
        });
      } else {
        throw new Error(result.message || 'Errore durante l\'invio');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: "destructive",
        title: "Errore invio",
        description: error.message || "Si è verificato un errore. Riprova più tardi.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Grazie!</h2>
          <p className="text-gray-600 mb-6">
            La tua richiesta è stata inviata con successo. Ti contatteremo al più presto!
          </p>
          <button
            onClick={() => {
              setIsSuccess(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                companyName: '',
                products: { milone: false, belpaese: false, contoTerzi: false },
                liquorTypes: [],
                bottleFormats: [],
                quantityRange: '',
                recipeType: '',
                recipeDetails: '',
                message: ''
              });
            }}
            className="text-amber-600 hover:text-amber-700 font-medium"
            data-testid="new-contact-btn"
          >
            Nuovo contatto
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-2xl mx-auto" data-testid="fiere-form-container">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_digital-services-35/artifacts/x7rk46mm_10000477-logo_fix.png" 
            alt="Il Tuo Amaro" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-testid="form-title">
            Marca 2026
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Compila il form per ricevere maggiori informazioni
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dati Contatto */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Nome */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Nome e Cognome *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="Mario Rossi"
                data-testid="input-name"
              />
            </div>

            {/* Azienda */}
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1.5">
                Azienda
              </label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="Nome Azienda S.r.l."
                data-testid="input-company"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="mario@azienda.it"
                data-testid="input-email"
              />
            </div>

            {/* Telefono */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                Telefono *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="+39 333 1234567"
                data-testid="input-phone"
              />
            </div>
          </div>

          {/* Prodotti Interessati */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              🏷️ Prodotti interessati *
            </label>
            <div className="grid sm:grid-cols-3 gap-3">
              {/* Milone */}
              <label 
                className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.products.milone 
                    ? 'border-amber-500 bg-amber-50 text-amber-700' 
                    : 'border-gray-200 hover:border-amber-300'
                }`}
                data-testid="checkbox-milone"
              >
                <input
                  type="checkbox"
                  checked={formData.products.milone}
                  onChange={() => handleProductCheckbox('milone')}
                  className="sr-only"
                />
                <span className="font-medium">{formData.products.milone && '✓ '}Milone</span>
              </label>

              {/* BelPaese */}
              <label 
                className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.products.belpaese 
                    ? 'border-amber-500 bg-amber-50 text-amber-700' 
                    : 'border-gray-200 hover:border-amber-300'
                }`}
                data-testid="checkbox-belpaese"
              >
                <input
                  type="checkbox"
                  checked={formData.products.belpaese}
                  onChange={() => handleProductCheckbox('belpaese')}
                  className="sr-only"
                />
                <span className="font-medium">{formData.products.belpaese && '✓ '}BelPaese</span>
              </label>

              {/* Conto Terzi */}
              <label 
                className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  formData.products.contoTerzi 
                    ? 'border-amber-500 bg-amber-50 text-amber-700' 
                    : 'border-gray-200 hover:border-amber-300'
                }`}
                data-testid="checkbox-contoterzi"
              >
                <input
                  type="checkbox"
                  checked={formData.products.contoTerzi}
                  onChange={() => handleProductCheckbox('contoTerzi')}
                  className="sr-only"
                />
                <span className="font-medium">{formData.products.contoTerzi && '✓ '}Conto Terzi</span>
              </label>
            </div>
          </div>

          {/* Tipi di Liquore */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              🍾 Tipi di liquore di interesse
            </label>
            <div className="flex flex-wrap gap-2">
              {liquorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleLiquorToggle(option.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.liquorTypes.includes(option.value)
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`liquor-${option.value}`}
                >
                  {formData.liquorTypes.includes(option.value) && '✓ '}{option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Formato Bottiglia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              📏 Formato bottiglia
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {formatOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleFormatToggle(option.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    formData.bottleFormats.includes(option.value)
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`format-${option.value}`}
                >
                  {formData.bottleFormats.includes(option.value) && '✓ '}{option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Quantità */}
          <div>
            <label htmlFor="quantityRange" className="block text-sm font-medium text-gray-700 mb-1.5">
              📦 Quantità richiesta
            </label>
            <select
              id="quantityRange"
              name="quantityRange"
              value={formData.quantityRange}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              data-testid="select-quantity"
            >
              <option value="">Seleziona quantità...</option>
              {quantityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Ricetta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              🧪 Ricetta
            </label>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              {recipeOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.recipeType === option.value
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                  data-testid={`recipe-${option.value}`}
                >
                  <input
                    type="radio"
                    name="recipeType"
                    value={option.value}
                    checked={formData.recipeType === option.value}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <span className="font-medium">{formData.recipeType === option.value && '✓ '}{option.label}</span>
                </label>
              ))}
            </div>
            {formData.recipeType === 'standard' && (
              <input
                type="text"
                name="recipeDetails"
                value={formData.recipeDetails}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="Specifica quale ricetta standard..."
                data-testid="input-recipe-details"
              />
            )}
          </div>

          {/* Note */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
              📝 Note aggiuntive
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all resize-none"
              placeholder="Inserisci eventuali note o richieste specifiche..."
              data-testid="input-message"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex items-center justify-center px-6 py-4 rounded-xl font-semibold text-white transition-all duration-200 ${
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg hover:shadow-xl'
            }`}
            data-testid="submit-btn"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Invia Richiesta
              </>
            )}
          </button>
        </form>

        {/* Privacy Note */}
        <p className="mt-6 text-xs text-gray-500 text-center">
          Inviando questo form acconsenti al trattamento dei tuoi dati personali secondo la nostra{' '}
          <a href="/privacy-policy" className="text-amber-600 hover:underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default FiereForm;
